import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { runDetailQueryOptions } from '@/lib/api/quality.js';
import type {
  CaseOutcome,
  DeliveryPage,
  OutcomeClassification,
  OutcomeHistoryPage,
  RunDetail,
} from '@/lib/api/quality-types.js';

import { formatDate } from './quality-presentation.js';
import {
  caseDisplayStatus,
  deliveryStateLabel,
  outcomeLabel,
} from './quality-outcome.js';

/** A history outcome as people saw it: an unconfirmed defect or QA error read Requires review. */
function shownHistoryOutcome(
  outcome: CaseOutcome,
  needsTriage: boolean,
  classification: OutcomeClassification | null,
): string {
  return outcomeLabel(
    caseDisplayStatus({
      outcome,
      needs_triage: needsTriage,
      classification,
      status: 'failed',
    }) as CaseOutcome,
  );
}

function deliveryItemLabel(value: string): string {
  return value
    .split('_')
    .map((part) => `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function nullableString(value: unknown): boolean {
  return value === null || typeof value === 'string';
}

/** Validate the fields this activity view consumes; newer string labels remain visible. */
function validHistory(value: unknown): value is OutcomeHistoryPage {
  return (
    record(value) &&
    nullableString(value.next_cursor) &&
    Array.isArray(value.items) &&
    value.items.every(
      (item: unknown) =>
        record(item) &&
        typeof item.request_id === 'string' &&
        typeof item.case_id === 'string' &&
        typeof item.old_outcome === 'string' &&
        typeof item.new_outcome === 'string' &&
        typeof item.comment === 'string' &&
        record(item.actor) &&
        nullableString(item.actor.display_name) &&
        typeof item.applied_at === 'string' &&
        typeof item.report_revision === 'number' &&
        Number.isSafeInteger(item.report_revision) &&
        item.report_revision >= 0,
    )
  );
}

function validReceipt(value: unknown): boolean {
  return (
    value === null ||
    (record(value) &&
      typeof value.id === 'string' &&
      typeof value.status === 'string' &&
      typeof value.verified_at === 'string')
  );
}

function validDelivery(value: unknown): value is DeliveryPage {
  return (
    record(value) &&
    nullableString(value.next_cursor) &&
    record(value.summary) &&
    typeof value.summary.state === 'string' &&
    record(value.summary.counts) &&
    Object.values(value.summary.counts).every(
      (count) =>
        typeof count === 'number' && Number.isSafeInteger(count) && count >= 0,
    ) &&
    Array.isArray(value.items) &&
    value.items.every(
      (item: unknown) =>
        record(item) &&
        typeof item.delivery_id === 'string' &&
        typeof item.destination_label === 'string' &&
        typeof item.state === 'string' &&
        nullableString(item.reason_code) &&
        validReceipt(item.external_receipt) &&
        validReceipt(item.retained_external_receipt),
    )
  );
}

/** Compact delivery state and cursor-paged manual correction history. */
export function OutcomeActivity({
  runId,
  initial,
}: {
  runId: string;
  initial: RunDetail;
}): JSX.Element | null {
  const [correctionCursor, setCorrectionCursor] = useState<string>();
  const [deliveryCursor, setDeliveryCursor] = useState<string>();
  const [correctionRetried, setCorrectionRetried] = useState(false);
  const [deliveryRetried, setDeliveryRetried] = useState(false);
  const correctionPage = useQuery({
    ...runDetailQueryOptions(runId, {
      correctionCursor,
      limit: 25,
    }),
    enabled: Boolean(correctionCursor),
  });
  const deliveryPage = useQuery({
    ...runDetailQueryOptions(runId, {
      deliveryCursor,
      limit: 25,
    }),
    enabled: Boolean(deliveryCursor),
  });
  const initialHistory = validHistory(initial.outcome_history)
    ? initial.outcome_history
    : undefined;
  const initialDelivery = validDelivery(initial.delivery)
    ? initial.delivery
    : undefined;
  const nextHistory = validHistory(correctionPage.data?.outcome_history)
    ? correctionPage.data?.outcome_history
    : undefined;
  const nextDelivery = validDelivery(deliveryPage.data?.delivery)
    ? deliveryPage.data?.delivery
    : undefined;
  const [retainedHistory, setRetainedHistory] = useState(initialHistory);
  const [retainedDelivery, setRetainedDelivery] = useState(initialDelivery);

  useEffect(() => setCorrectionRetried(false), [initial.outcome_history]);
  useEffect(() => setDeliveryRetried(false), [initial.delivery]);
  useEffect(() => {
    if (!correctionCursor && initialHistory) setRetainedHistory(initialHistory);
  }, [correctionCursor, initialHistory]);
  useEffect(() => {
    if ((correctionCursor || correctionRetried) && nextHistory)
      setRetainedHistory(nextHistory);
  }, [correctionCursor, correctionRetried, nextHistory]);
  useEffect(() => {
    if (!deliveryCursor && initialDelivery)
      setRetainedDelivery(initialDelivery);
  }, [deliveryCursor, initialDelivery]);
  useEffect(() => {
    if ((deliveryCursor || deliveryRetried) && nextDelivery)
      setRetainedDelivery(nextDelivery);
  }, [deliveryCursor, deliveryRetried, nextDelivery]);

  const useCorrectionResponse = Boolean(correctionCursor || correctionRetried);
  const useDeliveryResponse = Boolean(deliveryCursor || deliveryRetried);
  const history =
    (useCorrectionResponse ? nextHistory : initialHistory) ?? retainedHistory;
  const delivery =
    (useDeliveryResponse ? nextDelivery : initialDelivery) ?? retainedDelivery;
  const correctionInvalid = useCorrectionResponse
    ? correctionPage.data !== undefined && !nextHistory
    : initial.outcome_history !== undefined && !initialHistory;
  const deliveryInvalid = useDeliveryResponse
    ? deliveryPage.data !== undefined && !nextDelivery
    : initial.delivery !== undefined && !initialDelivery;
  const correctionError = correctionPage.isError || correctionInvalid;
  const deliveryError = deliveryPage.isError || deliveryInvalid;
  const retryCorrection = (): void => {
    setCorrectionRetried(true);
    void correctionPage.refetch();
  };
  const retryDelivery = (): void => {
    setDeliveryRetried(true);
    void deliveryPage.refetch();
  };
  if (!history && !delivery && !correctionError && !deliveryError) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Outcome activity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {delivery ? (
          <section className="space-y-2" aria-label="Delivery summary">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium">Delivery</span>
              <Badge
                variant={
                  delivery.summary.state === 'delivered' ? 'success' : 'outline'
                }
              >
                {deliveryStateLabel(delivery.summary.state)}
              </Badge>
              {Object.entries(delivery.summary.counts).map(([state, count]) =>
                count > 0 ? (
                  <span key={state} className="text-xs text-muted-foreground">
                    {count} {deliveryItemLabel(state)}
                  </span>
                ) : null,
              )}
            </div>
            {delivery.items.map((item) => (
              <div
                key={item.delivery_id}
                className="flex flex-wrap gap-x-3 text-xs"
              >
                <span>{item.destination_label}</span>
                <span className="text-muted-foreground">
                  {deliveryItemLabel(item.state)}
                </span>
                {item.reason_code ? (
                  <span className="text-warning-700">
                    {deliveryItemLabel(item.reason_code)}
                  </span>
                ) : null}
                {item.external_receipt ? (
                  <span className="text-muted-foreground">
                    Receipt {item.external_receipt.id} ·{' '}
                    {item.external_receipt.status} · verified{' '}
                    {formatDate(item.external_receipt.verified_at)}
                  </span>
                ) : null}
                {item.retained_external_receipt ? (
                  <span className="text-muted-foreground">
                    Prior receipt {item.retained_external_receipt.id} ·{' '}
                    {item.retained_external_receipt.status} · verified{' '}
                    {formatDate(item.retained_external_receipt.verified_at)}
                  </span>
                ) : null}
              </div>
            ))}
            {deliveryError ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={deliveryPage.isFetching}
                onClick={retryDelivery}
              >
                Retry delivery page
              </Button>
            ) : delivery.next_cursor ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={deliveryPage.isFetching}
                onClick={() =>
                  setDeliveryCursor(delivery.next_cursor ?? undefined)
                }
              >
                Next delivery page
              </Button>
            ) : null}
          </section>
        ) : null}
        {history ? (
          <section className="space-y-2" aria-label="Correction history">
            <p className="text-sm font-medium">Correction history</p>
            {history.items.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No manual corrections.
              </p>
            ) : (
              history.items.map((item) => (
                <div
                  key={item.request_id}
                  className="rounded-md border border-border p-3 text-sm"
                >
                  <p>
                    <span className="font-medium">{item.case_id}</span>:{' '}
                    {shownHistoryOutcome(
                      item.old_outcome,
                      item.old_needs_triage,
                      item.prior_classification,
                    )}{' '}
                    →{' '}
                    {shownHistoryOutcome(
                      item.new_outcome,
                      item.new_needs_triage,
                      item.classification,
                    )}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.comment} · {item.actor.display_name ?? 'Unknown user'}{' '}
                    · {formatDate(item.applied_at)} · report revision{' '}
                    {item.report_revision}
                  </p>
                </div>
              ))
            )}
            {correctionError ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={correctionPage.isFetching}
                onClick={retryCorrection}
              >
                Retry correction page
              </Button>
            ) : history.next_cursor ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={correctionPage.isFetching}
                onClick={() =>
                  setCorrectionCursor(history.next_cursor ?? undefined)
                }
              >
                Next correction page
              </Button>
            ) : null}
          </section>
        ) : null}
        {correctionError && !history ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={correctionPage.isFetching}
            onClick={retryCorrection}
          >
            Retry correction page
          </Button>
        ) : null}
        {deliveryError && !delivery ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={deliveryPage.isFetching}
            onClick={retryDelivery}
          >
            Retry delivery page
          </Button>
        ) : null}
        {correctionError || deliveryError ? (
          <p className="text-sm text-destructive">
            {correctionInvalid || deliveryInvalid
              ? 'Activity data is unavailable. The last valid page is retained.'
              : 'The next activity page could not be loaded.'}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}
